import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { servicesService, serviceBidsService, serviceCategoriesService } from '../services/api';
import '../styles/HomeWidgets.css';

interface Service {
  id: number;
  title: string;
  description: string;
  category: string;
  location: string;
  budget: number;
  currency: string;
  deadline: string;
  requester_name: string;
  status: string;
  is_urgent: boolean;
  bid_count: number;
  created_at: string;
}

interface DashboardStats {
  services: {
    total: number;
    open: number;
    assigned: number;
    recent: Service[];
  };
  myRequests: {
    total: number;
    pending: number;
    recent: Service[];
  };
  myBids: {
    total: number;
    pending: number;
    accepted: number;
    recent: any[];
  };
}

interface WidgetProps {
  title: string;
  icon: string;
  children: React.ReactNode;
  isLoading?: boolean;
}

interface ServiceCategory {
  id: number;
  name: string;
  description: string | null;
  deleted: boolean;
}

const Widget: React.FC<WidgetProps> = ({ title, icon, children, isLoading = false }) => {
  if (isLoading) {
    return (
      <div className="widget-card">
        <div className="loading-widget">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading {title.toLowerCase()}...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="widget-card">
      <div className="widget-header">
        <div className="widget-icon">{icon}</div>
        <h3 className="widget-title">{title}</h3>
      </div>
      <div className="widget-content">
        {children}
      </div>
    </div>
  );
};

const ServicesPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    services: { total: 0, open: 0, assigned: 0, recent: [] },
    myRequests: { total: 0, pending: 0, recent: [] },
    myBids: { total: 0, pending: 0, accepted: 0, recent: [] }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState({
    category: '',
    status: 'open',
    location: ''
  });
  const [categories, setCategories] = useState<ServiceCategory[]>([]);

  const isServiceProvider = user?.roles?.some(role => role.name === 'service_provider')


  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load available services
      const servicesResponse = await servicesService.getServices({
        ...filter,
        limit: 10
      });

      // Load user's service requests
      const myRequestsResponse = await servicesService.getMyServices({
        limit: 5
      });

      let myBidsData = { total: 0, pending: 0, accepted: 0, recent: [] };
      if (isServiceProvider) {
        try {
          const myBidsResponse = await serviceBidsService.getMyBids({
            limit: 5
          });
          myBidsData = {
            total: myBidsResponse.data?.length || 0,
            pending: myBidsResponse.data?.filter((bid: any) => bid.status === 'pending').length || 0,
            accepted: myBidsResponse.data?.filter((bid: any) => bid.status === 'accepted').length || 0,
            recent: myBidsResponse.data || []
          };
        } catch (bidError) {
          console.warn('Could not load bids:', bidError);
        }
      }

      setStats({
        services: {
          total: servicesResponse.data?.length || 0,
          open: servicesResponse.data?.filter((service: Service) => service.status === 'open').length || 0,
          assigned: servicesResponse.data?.filter((service: Service) => service.status === 'assigned').length || 0,
          recent: servicesResponse.data || []
        },
        myRequests: {
          total: myRequestsResponse.data?.length || 0,
          pending: myRequestsResponse.data?.filter((service: Service) => service.status === 'open').length || 0,
          recent: myRequestsResponse.data || []
        },
        myBids: myBidsData
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load services data');
    } finally {
      setLoading(false);
    }
  }, [filter, isServiceProvider]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await serviceCategoriesService.getCategories(false);
        setCategories(response.data || []);
      } catch (categoryError) {
        console.warn('Could not load categories:', categoryError);
        setCategories([]);
      }
    };

    loadCategories();
  }, []);

  const formatCurrency = (amount: number, currency: string = 'KSH') => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: currency === 'KSH' ? 'KES' : currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string, isUrgent: boolean = false) => {
    const baseClasses = "px-2 py-1 rounded text-xs font-medium";
    const urgentClass = isUrgent ? " border-2 border-red-400" : "";

    switch (status) {
      case 'open':
        return `${baseClasses} bg-green-100 text-green-800${urgentClass}`;
      case 'assigned':
        return `${baseClasses} bg-blue-100 text-blue-800${urgentClass}`;
      case 'completed':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case 'cancelled':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800${urgentClass}`;
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-red-700">
              <p className="font-medium">Error loading services</p>
              <p className="text-sm">{error}</p>
              <button
                onClick={loadDashboardData}
                className="mt-2 text-sm bg-red-100 hover:bg-red-200 px-3 py-1 rounded"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Services</h1>
              <p className="text-gray-600">Find services or offer your skills</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/services/request')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                Request Service
              </button>
              {!isServiceProvider && (
                <button
                  onClick={() => navigate('/apply-service-provider')}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium"
                >
                  Become Provider
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={filter.category}
                onChange={(e) => setFilter({ ...filter, category: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                <option value="open">Open</option>
                <option value="assigned">Assigned</option>
                <option value="completed">Completed</option>
                <option value="">All Statuses</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                value={filter.location}
                onChange={(e) => setFilter({ ...filter, location: e.target.value })}
                placeholder="Enter location"
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilter({ category: '', status: 'open', location: '' })}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Dashboard Widgets */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Available Services Widget */}
          <Widget title="Available Services" icon="🛠️" isLoading={loading}>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="text-2xl font-bold text-blue-600">
                  {stats.services.total}
                </div>
                <div className="text-sm text-gray-500">
                  {stats.services.open} open, {stats.services.assigned} assigned
                </div>
              </div>

              <div className="space-y-2">
                {stats.services.recent.slice(0, 3).map((service) => (
                  <Link
                    key={service.id}
                    to={`/services/${service.id}`}
                    className="block border border-gray-200 rounded p-2 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm truncate">
                          {service.title}
                        </h4>
                        <p className="text-xs text-gray-500 truncate">
                          {service.location} • {formatCurrency(service.budget)}
                        </p>
                      </div>
                      <span className={getStatusBadge(service.status, service.is_urgent)}>
                        {service.status}
                        {service.is_urgent && " 🔥"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-gray-400">
                        {service.bid_count} bids
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatDate(service.created_at)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>

              <Link
                to="/services/browse"
                className="block text-center bg-blue-50 hover:bg-blue-100 text-blue-600 py-2 rounded font-medium"
              >
                Browse All Services
              </Link>
            </div>
          </Widget>

          {/* My Service Requests Widget */}
          <Widget title="My Service Requests" icon="📋" isLoading={loading}>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="text-2xl font-bold text-green-600">
                  {stats.myRequests.total}
                </div>
                <div className="text-sm text-gray-500">
                  {stats.myRequests.pending} pending
                </div>
              </div>

              <div className="space-y-2">
                {stats.myRequests.recent.slice(0, 3).map((service) => (
                  <Link
                    key={service.id}
                    to={`/services/${service.id}`}
                    className="block border border-gray-200 rounded p-2 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm truncate">
                          {service.title}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {formatCurrency(service.budget)} • {formatDate(service.created_at)}
                        </p>
                      </div>
                      <span className={getStatusBadge(service.status, service.is_urgent)}>
                        {service.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="flex space-x-2">
                <Link
                  to="/services/my-requests"
                  className="flex-1 text-center bg-green-50 hover:bg-green-100 text-green-600 py-2 rounded font-medium"
                >
                  View All
                </Link>
                <Link
                  to="/services/request"
                  className="flex-1 text-center bg-blue-50 hover:bg-blue-100 text-blue-600 py-2 rounded font-medium"
                >
                  New Request
                </Link>
              </div>
            </div>
          </Widget>

          {/* My Bids Widget (Service Providers Only) */}
          {isServiceProvider && (
            <Widget title="My Bids" icon="💼" isLoading={loading}>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {stats.myBids.total}
                  </div>
                  <div className="text-sm text-gray-500">
                    {stats.myBids.pending} pending, {stats.myBids.accepted} accepted
                  </div>
                </div>

                <div className="space-y-2">
                  {stats.myBids.recent.slice(0, 3).map((bid: any) => (
                    <div key={bid.id} className="border border-gray-200 rounded p-2">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium text-sm truncate">
                            {bid.service_title}
                          </h4>
                          <p className="text-xs text-gray-500">
                            Bid: {formatCurrency(bid.bid_amount)}
                          </p>
                        </div>
                        <span className={getStatusBadge(bid.status)}>
                          {bid.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <Link
                  to="/services/my-bids"
                  className="block text-center bg-purple-50 hover:bg-purple-100 text-purple-600 py-2 rounded font-medium"
                >
                  View All Bids
                </Link>
              </div>
            </Widget>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/services/browse"
              className="bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg p-4 text-center transition-colors"
            >
              <div className="text-2xl mb-2">🔍</div>
              <div className="font-medium text-blue-900">Browse Services</div>
              <div className="text-sm text-blue-600">Find services you need</div>
            </Link>

            <Link
              to="/services/request"
              className="bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg p-4 text-center transition-colors"
            >
              <div className="text-2xl mb-2">➕</div>
              <div className="font-medium text-green-900">Request Service</div>
              <div className="text-sm text-green-600">Post a new service request</div>
            </Link>

            {isServiceProvider ? (
              <Link
                to="/services/my-bids"
                className="bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg p-4 text-center transition-colors"
              >
                <div className="text-2xl mb-2">💼</div>
                <div className="font-medium text-purple-900">My Bids</div>
                <div className="text-sm text-purple-600">Manage your bid submissions</div>
              </Link>
            ) : (
              <Link
                to="/apply-service-provider"
                className="bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg p-4 text-center transition-colors"
              >
                <div className="text-2xl mb-2">👨‍💼</div>
                <div className="font-medium text-orange-900">Become Provider</div>
                <div className="text-sm text-orange-600">Apply to offer services</div>
              </Link>
            )}

            <Link
              to="/services/categories"
              className="bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg p-4 text-center transition-colors"
            >
              <div className="text-2xl mb-2">📊</div>
              <div className="font-medium text-gray-900">Categories</div>
              <div className="text-sm text-gray-600">Explore service categories</div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;